import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import ProtectedRoute from '@/components/ProtectedRoute'
import { ProaResultGlobal } from '@/components/ProaResultGlobal'
import { ClientResult } from '@/components/ClientResult'
import { VarianceGauge, PerformanceHeatmap, DonutChart } from '@/lib/components/charts'
import { getGlobalPerformance, getClients } from '@/api/sig-data'
import { Download } from 'lucide-react'
import { exportToPDF, exportToCSV, formatDataForExport } from '@/lib/utils/export-utils'
import type { GlobalPerformanceMetrics } from '@/types/financial'

function ResultsContent() {
  const { user } = useAuth()
  const [globalMetrics, setGlobalMetrics] = useState<GlobalPerformanceMetrics | null>(null)
  const [heatmapData, setHeatmapData] = useState<any[]>([])
  const [donutData, setDonutData] = useState<any[]>([])
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    const loadData = async () => {
      try {
        const currentYear = new Date().getFullYear().toString()

        // Load global metrics
        const metrics = await getGlobalPerformance(currentYear)
        setGlobalMetrics(metrics)

        // Load client data for heatmap
        const clients = await getClients(currentYear, { limit: 50 })

        // Transform for heatmap
        const heatmapTransformed = clients
          .filter((c: any) => c.priority)
          .slice(0, 12)
          .map((client: any) => ({
            client: client.clientName,
            metrics: {
              'Revenue': client.proaVariancePercent || 0,
              'Profit': ((client.resultado - client.proa) / client.proa) * 100 || 0,
              'Margin': ((client.margemContrib / client.faturamento) * 100) - 50 || 0
            }
          }))
        setHeatmapData(heatmapTransformed)

        // Transform for donut chart (category breakdown)
        // Use client aggregation as metrics doesn't have detailed breakdown
        const totalRealizado = metrics.totalRealized || 0
        const totalPROA = metrics.totalPROA || 0
        const variance = totalRealizado - totalPROA

        const categories = [
          { name: 'Realizado', value: Math.abs(totalRealizado) },
          { name: 'PROA (Meta)', value: Math.abs(totalPROA) },
          { name: 'Variância', value: Math.abs(variance) }
        ]
        setDonutData(categories)
      } catch (error) {
        console.error('Error loading chart data:', error)
      }
    }

    loadData()
  }, [])

  const handleExportPDF = async () => {
    try {
      setIsExporting(true)
      await exportToPDF('results-content', 'resultados-proa')
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  const handleExportCSV = async () => {
    try {
      setIsExporting(true)
      const currentYear = new Date().getFullYear().toString()
      const clients = await getClients(currentYear, { limit: 50 })
      const formatted = formatDataForExport(clients)
      exportToCSV(formatted, 'resultados-proa')
    } catch (error) {
      console.error('Export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    
      <div id="results-content" className="space-y-6 sm:space-y-8">
        {/* Header with Export Buttons */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
              Resultados
            </h1>
            <p className="font-body text-brand-gray-light">
              Performance e resultados financeiros dos clientes
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center gap-4">
            <div className="flex items-center space-x-2 text-sm text-brand-gray">
              <div className="w-2 h-2 bg-brand-primary rounded-full animate-pulse"></div>
              <span>Última atualização: agora</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleExportCSV}
                disabled={isExporting}
                className="flex items-center gap-2 px-3 py-2 bg-brand-dark-lighter hover:bg-brand-dark text-white rounded-monumenta text-sm transition-colors disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                CSV
              </button>
              <button
                onClick={handleExportPDF}
                disabled={isExporting}
                className="flex items-center gap-2 px-3 py-2 bg-brand-primary hover:bg-brand-primary/80 text-white rounded-monumenta text-sm transition-colors disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                PDF
              </button>
            </div>
          </div>
        </div>

        {/* Consolidated Performance Gauge */}
        {globalMetrics && (
          <div className="card">
            <h2 className="font-display text-xl font-bold text-white mb-4">Performance Geral PROA 2025</h2>
            <div className="flex justify-center">
              <VarianceGauge
                actual={globalMetrics.totalRealized || 0}
                target={globalMetrics.totalPROA || 0}
                label="Performance Global"
                size="lg"
                showPercentage={true}
              />
            </div>
          </div>
        )}

        {/* PROA Result Global Component */}
        <ProaResultGlobal />

        {/* Client Results Component */}
        <ClientResult />

        {/* Performance Heatmap */}
        {heatmapData.length > 0 && (
          <div className="card">
            <h2 className="font-display text-xl font-bold text-white mb-4">Performance por Cliente e Métrica</h2>
            <PerformanceHeatmap
              data={heatmapData}
              selectedMetrics={['Revenue', 'Profit', 'Margin']}
            />
          </div>
        )}

        {/* Variance Breakdown Donut Chart */}
        {donutData.length > 0 && (
          <div className="card">
            <h2 className="font-display text-xl font-bold text-white mb-4">Distribuição por Categoria</h2>
            <DonutChart
              data={donutData}
              height={400}
              centerText="Breakdown Financeiro"
            />
          </div>
        )}
      </div>
    
  )
}

export default function ResultsPage() {
  return (
    <ProtectedRoute>
      <ResultsContent />
    </ProtectedRoute>
  )
}
